import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Candle } from '@pipyar/core';
import { api } from '@/lib/api';
import { useSymbol } from '@/lib/useSymbol';
import { useFeedHealth } from '@/lib/useFeedHealth';
import { useQuote } from '@/features/alerts/api';
import { cn } from '@/lib/utils';

/**
 * The selected pair's live price, in the top bar beside the logo.
 *
 * Reads the quote the live stream keeps in the cache, so it costs nothing
 * extra on the network. Each tick tints the number green or red for a moment
 * by which way it moved, and the whole readout dims when the feed is not
 * live, so a frozen price never passes for a current one.
 *
 * Beside it, the change since the day opened: the open of the current D1 bar,
 * which is cut at the 17:00 New York session boundary (see ingest/sessions.ts)
 * like every daily candle in the app, not at local midnight.
 */
export function LivePrice() {
  const { symbol } = useSymbol();
  const quote = useQuote(symbol).data;
  const { state } = useFeedHealth();
  const live = state === 'live';

  const price = quote && quote.symbol === symbol ? quote.price : null;
  const digits = quote?.digits ?? 2;

  // The day's open only changes once a day; a minute's refresh is plenty to
  // pick up the new session promptly. The response is a bar or two.
  const day = useQuery({
    queryKey: ['day-open', symbol],
    queryFn: () =>
      api.get<{ candles: Candle[] }>(
        `/api/market/candles?symbol=${encodeURIComponent(symbol!)}&timeframe=D1&limit=1`,
      ),
    enabled: Boolean(symbol),
    refetchInterval: 60_000,
    staleTime: 55_000,
  });
  const dayCandles = day.data?.candles ?? [];
  const dayBar = dayCandles.length > 0 ? dayCandles[dayCandles.length - 1]! : null;
  const dayOpen = dayBar?.open ?? null;
  // The stored bar can be up to a minute behind; folding in the live quote
  // shows a new high or low the moment it prints. The quote's own high/low is
  // the current M1 bar's, so a spike between ticks is caught too.
  const liveQuote = quote && quote.symbol === symbol ? quote : null;
  const dayHigh =
    dayBar === null ? null : Math.max(dayBar.high, liveQuote?.high ?? -Infinity, liveQuote?.price ?? -Infinity);
  const dayLow =
    dayBar === null ? null : Math.min(dayBar.low, liveQuote?.low ?? Infinity, liveQuote?.price ?? Infinity);
  const change = price !== null && dayOpen !== null ? price - dayOpen : null;
  const changePct = change !== null && dayOpen ? (change / dayOpen) * 100 : null;

  const previous = useRef<number | null>(null);
  const [move, setMove] = useState<'up' | 'down' | null>(null);
  useEffect(() => {
    if (price === null) return;
    const prev = previous.current;
    previous.current = price;
    if (prev === null || prev === price) return;
    setMove(price > prev ? 'up' : 'down');
    const id = setTimeout(() => setMove(null), 600);
    return () => clearTimeout(id);
  }, [price]);

  // Reset between pairs, so switching does not flash a "move" across them.
  useEffect(() => {
    previous.current = null;
    setMove(null);
  }, [symbol]);

  if (!symbol) return null;

  return (
    <div
      className={cn(
        'flex items-baseline gap-1.5 rounded-lg border border-border/70 bg-surface/60 px-2.5 py-1',
        !live && 'opacity-60',
      )}
      title={live ? undefined : 'Not live'}
    >
      <span className="hidden text-[11px] font-medium text-muted-foreground sm:inline">{symbol}</span>
      <span
        className={cn(
          'tabular text-sm font-semibold transition-colors duration-500',
          move === 'up' ? 'text-bull' : move === 'down' ? 'text-bear' : 'text-foreground',
        )}
      >
        {price === null ? '—' : price.toFixed(digits)}
      </span>
      {change !== null && changePct !== null && (
        <span
          className={cn(
            'tabular text-[11px] font-medium',
            change > 0 ? 'text-bull' : change < 0 ? 'text-bear' : 'text-muted-foreground',
          )}
          title="Change since the day opened (17:00 New York)"
        >
          {change > 0 ? '+' : ''}
          {change.toFixed(digits)}
          <span className="hidden sm:inline">
            {' '}({changePct > 0 ? '+' : ''}
            {changePct.toFixed(2)}%)
          </span>
        </span>
      )}
      {dayHigh !== null && dayLow !== null && (
        // Hidden on narrow screens: the top bar has no room for it there.
        <span className="hidden items-baseline gap-1.5 text-[11px] md:flex" title="Day high / low">
          <span className="text-muted-foreground">H</span>
          <span className="tabular font-medium">{dayHigh.toFixed(digits)}</span>
          <span className="text-muted-foreground">L</span>
          <span className="tabular font-medium">{dayLow.toFixed(digits)}</span>
        </span>
      )}
    </div>
  );
}
